import React from 'react';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Target, TrendingUp, ShoppingCart, CreditCard, CheckCircle, ArrowDown } from 'lucide-react';
import { useAnalytics } from '../../hooks/useAnalytics';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip
} from 'recharts';

interface ConversionAnalysisProps {
  dateRange: {
    from: Date;
    to: Date;
  };
  projectId?: string;
}

export function ConversionAnalysis({ dateRange, projectId }: ConversionAnalysisProps) {
  const analytics = useAnalytics(projectId);
  const { data: conversionData, isLoading, error } = analytics.useConversions(dateRange);
  
  const dateRangeText = `${dateRange.from.toLocaleDateString()} - ${dateRange.to.toLocaleDateString()}`;
  const isProjectSelected = !!projectId;

  // Use real data if available, otherwise fallback to mock data
  const conversions = conversionData?.conversions;
  
  const conversionMetrics = [
    {
      title: 'Overall Conversion Rate',
      value: conversions?.overallConversionRate || '3.24%',
      change: '+0.8%', // TODO: Calculate change from previous period
      trend: 'up',
      icon: Target,
      color: 'blue'
    },
    {
      title: 'Goal Completions',
      value: conversions?.totalConversions?.toLocaleString() || '1,234',
      change: '+15.2%', // TODO: Calculate change from previous period
      trend: 'up',
      icon: CheckCircle,
      color: 'green'
    },
    {
      title: 'E-commerce Conversion',
      value: conversions?.ecommerceConversions?.toLocaleString() || '867',
      change: '+0.5%', // TODO: Calculate change from previous period
      trend: 'up',
      icon: ShoppingCart,
      color: 'purple'
    },
    {
      title: 'Revenue per Visitor',
      value: conversions?.revenuePerVisitor ? `$${conversions.revenuePerVisitor.toFixed(2)}` : '$12.45',
      change: '+8.3%', // TODO: Calculate change from previous period
      trend: 'up',
      icon: CreditCard,
      color: 'orange'
    }
  ];

  const funnelSteps = conversions?.funnelSteps || [
    { step: 'Landing Page Views', users: 10000, percentage: 100, dropOff: 0 },
    { step: 'Product Page Views', users: 6500, percentage: 65, dropOff: 35 },
    { step: 'Add to Cart', users: 2600, percentage: 26, dropOff: 60 },
    { step: 'Checkout Started', users: 1300, percentage: 13, dropOff: 50 },
    { step: 'Purchase Completed', users: 324, percentage: 3.24, dropOff: 75 }
  ];

  const topGoals = conversions?.topEvents?.slice(0, 5).map(event => ({
    goal: event.event.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    completions: event.count,
    rate: event.rate,
    value: '$0.00' // TODO: Add goal values to GA4 data
  })) || [
    { goal: 'Newsletter Signup', completions: 456, rate: '4.56%', value: '$2.30' },
    { goal: 'Contact Form', completions: 234, rate: '2.34%', value: '$15.60' },
    { goal: 'Download PDF', completions: 189, rate: '1.89%', value: '$1.20' },
    { goal: 'Video Watched', completions: 567, rate: '5.67%', value: '$0.80' },
    { goal: 'Free Trial', completions: 89, rate: '0.89%', value: '$45.00' }
  ];

  const conversionBySource = conversions?.conversionBySource || [
    { source: 'Organic Search', sessions: 15234, conversions: 493, rate: '3.24%' },
    { source: 'Direct', sessions: 9876, conversions: 296, rate: '3.00%' },
    { source: 'Social Media', sessions: 6543, conversions: 131, rate: '2.00%' },
    { source: 'Email', sessions: 2109, conversions: 105, rate: '4.98%' },
    { source: 'Paid Search', sessions: 1876, conversions: 94, rate: '5.01%' }
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Conversion Analysis</h2>
            <p className="text-sm text-gray-600">Track your conversion funnel and goal completions</p>
          </div>
          <div className="text-sm text-gray-500">{dateRangeText}</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="p-6">
              <div className="animate-pulse">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-2"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Conversion Analysis</h2>
            <p className="text-sm text-gray-600">Track your conversion funnel and goal completions</p>
          </div>
          <div className="text-sm text-gray-500">{dateRangeText}</div>
        </div>
        <Card className="p-6 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700">
          <div className="text-center">
            <p className="text-red-600 dark:text-red-400">Error loading conversion data</p>
            <p className="text-sm text-red-500 dark:text-red-400 mt-1">{error.message}</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Conversion Analysis</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300">Track your conversion funnel and goal completions</p>
        </div>
        <div className="flex items-center space-x-2">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {dateRangeText} {isProjectSelected && `• Project: ${projectId}`}
          </div>
          {conversionData?.dataSource && (
            <Badge variant={conversionData.dataSource === 'ga4_real' ? 'success' : 'secondary'}>
              {conversionData.dataSource === 'ga4_real' ? 'Real Data' : 'Sample Data'}
            </Badge>
          )}
        </div>
      </div>

      {/* Conversion Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {conversionMetrics.map((metric, index) => (
          <Card key={index} className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{metric.title}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{metric.value}</p>
                <div className="flex items-center mt-2">
                  <TrendingUp className="w-4 h-4 text-green-600 mr-1" />
                  <span className="text-sm font-medium text-green-600">{metric.change}</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">vs last period</span>
                </div>
              </div>
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                metric.color === 'blue' ? 'bg-blue-100 dark:bg-blue-900/30' :
                metric.color === 'green' ? 'bg-green-100 dark:bg-green-900/30' :
                metric.color === 'purple' ? 'bg-purple-100 dark:bg-purple-900/30' :
                'bg-orange-100 dark:bg-orange-900/30'
              }`}>
                <metric.icon className={`w-6 h-6 ${
                  metric.color === 'blue' ? 'text-blue-600 dark:text-blue-400' :
                  metric.color === 'green' ? 'text-green-600 dark:text-green-400' :
                  metric.color === 'purple' ? 'text-purple-600 dark:text-purple-400' :
                  'text-orange-600 dark:text-orange-400'
                }`} />
              </div>
            </div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Key Event Funnel */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Key Event Funnel</h3>
            {conversionData?.dataSource === 'ga4_real' && (
              <Badge variant="success" className="text-xs">
                Real GA4 Data
              </Badge>
            )}
          </div>
          <div className="space-y-4">
            {funnelSteps.map((step: any, index: number) => (
              <div key={index} className="relative">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    {step.icon && (
                      <span className="text-lg mr-2" role="img" aria-label="step-icon">
                        {step.icon}
                      </span>
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-white">{step.step}</span>
                        {step.isKeyEvent && (
                          <Badge variant="default" className="text-xs">Key Event</Badge>
                        )}
                      </div>
                      {step.eventName && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">Event: {step.eventName}</div>
                      )}
                      {step.keyEvents > 0 && (
                        <div className="text-xs text-green-600">Key Events: {step.keyEvents}</div>
                      )}
                      {step.pagePath && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">Page: {step.pagePath}</div>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="font-semibold">{step.users.toLocaleString()}</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">({step.percentage}%)</span>
                  </div>
                </div>
                <div className="relative">
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-8">
                    <div 
                      className={`h-8 rounded-full flex items-center justify-center text-white text-sm font-medium ${
                        step.eventName === 'conversion' 
                          ? 'bg-gradient-to-r from-green-600 to-emerald-600'
                          : index === 0 
                            ? 'bg-gradient-to-r from-blue-600 to-blue-700'
                            : 'bg-gradient-to-r from-blue-600 to-purple-600'
                      }`}
                      style={{ width: `${Math.max(step.percentage, 5)}%` }}
                    >
                      {step.percentage >= 10 && `${step.percentage}%`}
                    </div>
                  </div>
                  {index < funnelSteps.length - 1 && step.dropOff > 0 && (
                    <div className="absolute -bottom-6 right-0 flex items-center text-xs text-red-600">
                      <ArrowDown className="w-3 h-3 mr-1" />
                      {step.dropOff}% drop-off
                    </div>
                  )}
                </div>
                {index < funnelSteps.length - 1 && <div className="h-8"></div>}
              </div>
            ))}
          </div>
        </Card>

        {/* Top Goals */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Goal Performance</h3>
          <div className="space-y-4">
            {topGoals.map((goal, index) => (
                              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 dark:text-white">{goal.goal}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Rate: {goal.rate}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900 dark:text-white">{goal.completions}</p>
                    <p className="text-sm text-green-600 dark:text-green-400">{goal.value}</p>
                  </div>
                </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Conversion by Source */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Conversion Rate by Traffic Source</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
                              <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Source</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Sessions</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Conversions</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Conversion Rate</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Performance</th>
                </tr>
            </thead>
            <tbody>
              {conversionBySource.map((source, index) => (
                <tr key={index} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">{source.source}</td>
                  <td className="text-right py-3 px-4 text-gray-900 dark:text-gray-100">{source.sessions.toLocaleString()}</td>
                  <td className="text-right py-3 px-4 text-gray-900 dark:text-gray-100">{source.conversions}</td>
                  <td className="text-right py-3 px-4 font-semibold text-gray-900 dark:text-white">{source.rate}</td>
                  <td className="text-right py-3 px-4">
                    <div className="w-16 bg-gray-200 dark:bg-gray-600 rounded-full h-2 ml-auto">
                      <div 
                        className="bg-green-600 dark:bg-green-400 h-2 rounded-full" 
                        style={{ width: `${(parseFloat(source.rate) / 6) * 100}%` }}
                      ></div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Debug Section - Raw Funnel Data */}
      {conversionData?.dataSource === 'ga4_real' && conversions && (
        <Card className="p-6 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">GA4 Event Debug (Real Data)</h3>
            {conversions.totalKeyEvents && (
              <Badge variant="success">
                Total Key Events: {conversions.totalKeyEvents}
              </Badge>
            )}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div>
              <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Key Events Only</h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {conversions.keyEventsOnly?.map((event: any, index: number) => (
                  <div key={index} className="flex justify-between text-sm p-2 bg-green-50 dark:bg-green-900/30 rounded">
                    <span className="truncate text-gray-900 dark:text-gray-100">{event.event}</span>
                    <div className="text-right">
                      <div className="font-medium text-green-700 dark:text-green-400">{event.keyEvents} keys</div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">{event.users} users</div>
                    </div>
                  </div>
                )) || <div className="text-sm text-gray-500 dark:text-gray-400">No Key Events</div>}
              </div>
            </div>
            <div>
              <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">All GA4 Events</h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {conversions.allEventsDebug?.map((event: any, index: number) => (
                  <div key={index} className="flex justify-between text-sm">
                    <span className="truncate text-gray-900 dark:text-gray-100">{event.event}</span>
                    <span className="font-medium text-gray-900 dark:text-white">{event.users}</span>
                  </div>
                )) || <div className="text-sm text-gray-500 dark:text-gray-400">No events data</div>}
              </div>
            </div>
            <div>
              <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Filtered Funnel Events</h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {conversions.rawFunnelEvents?.map((event: any, index: number) => (
                  <div key={index} className="flex justify-between text-sm">
                    <span className="text-gray-900 dark:text-gray-100">{event.event}</span>
                    <span className="font-medium text-gray-900 dark:text-white">{event.users} users</span>
                  </div>
                )) || <div className="text-sm text-gray-500 dark:text-gray-400">No funnel events</div>}
              </div>
            </div>
            <div>
              <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Top Journey Pages</h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {conversions.rawJourneyPages?.map((page: any, index: number) => (
                  <div key={index} className="flex justify-between text-sm">
                    <span className="truncate text-gray-900 dark:text-gray-100">{page.page}</span>
                    <span className="font-medium text-gray-900 dark:text-white">{page.users} users</span>
                  </div>
                )) || <div className="text-sm text-gray-500 dark:text-gray-400">No page data</div>}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Conversion Trends Chart */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Conversion Trends</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">Conversion rate over time</p>
          </div>
          {conversionData?.dataSource && (
            <Badge variant={conversionData.dataSource === 'ga4_real' ? 'success' : 'secondary'}>
              {conversionData.dataSource === 'ga4_real' ? 'Real Data' : 'Sample Data'}
            </Badge>
          )}
        </div>

        {conversionData?.charts?.conversionTrends?.length > 0 ? (
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={conversionData.charts.conversionTrends}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--grid-color)" />
                <XAxis 
                  dataKey="date" 
                  stroke="var(--text-muted)"
                  fontSize={12}
                />
                <YAxis 
                  stroke="var(--text-muted)" 
                  fontSize={12}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'var(--card-bg)',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    color: 'var(--text-primary)'
                  }}
                  formatter={(value: number, name: string) => [
                    `${value}%`,
                    name === 'conversionRate' ? 'Conversion Rate' : name
                  ]}
                  labelStyle={{ color: 'var(--text-primary)', fontWeight: 600 }}
                />
                <defs>
                  <linearGradient id="conversionGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--color-green)" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="var(--color-green)" stopOpacity={0.05}/>
                  </linearGradient>
                </defs>
                <Area
                  type="monotone"
                  dataKey="conversionRate"
                  stroke="var(--color-green)"
                  strokeWidth={3}
                  fill="url(#conversionGradient)"
                  dot={{ fill: 'var(--color-green)', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, stroke: 'var(--color-green)', strokeWidth: 2 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-64 bg-gray-50 dark:bg-gray-800 rounded-lg flex items-center justify-center">
            <div className="text-center">
              <Target className="w-12 h-12 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500 dark:text-gray-400">
                {isLoading ? 'Loading conversion trends...' : 'No conversion trend data available'}
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                Conversion trends will appear here once data is available
              </p>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
} 